import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useWorkspace } from "@/context/WorkspaceContext";
import { errorMessage } from "@/lib/api";
import { SicPicker } from "./SicPicker";

const field = (label, el) => <div className="space-y-1.5"><Label>{label}</Label>{el}</div>;

export function NewSearchDialog({ open, onOpenChange, onCreated }) {
  const { api } = useWorkspace();
  const [f, setF] = useState({ business_type: "", location: "", radius: 10, radius_unit: "mi", exclude_holdings: true });
  const [sic, setSic] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/stage1/searches", { ...f, radius: Number(f.radius) || null, sic_codes: sic.map((s) => s.code) });
      toast.success("Searching Companies House…"); onCreated(data); onOpenChange(false); setSic([]);
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="stage1-new-dialog">
        <DialogHeader>
          <DialogTitle>New Stage 1 search</DialogTitle>
          <DialogDescription>Lists every active company on the Companies House register under the chosen trade codes within the radius. Free — no AI is used until you research companies.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {field("Type of business you want (used when researching)", <Input value={f.business_type} onChange={set("business_type")} placeholder="e.g. security integrator" data-testid="stage1-business-type" />)}
          {field("Trade (SIC) codes", <SicPicker value={sic} onChange={setSic} />)}
          <div className="grid gap-3 sm:grid-cols-3">
            {field("Town or UK postcode", <Input value={f.location} onChange={set("location")} placeholder="e.g. Manchester" data-testid="stage1-location" />)}
            {field("Radius", <Input type="number" min="1" max="100" value={f.radius} onChange={set("radius")} data-testid="stage1-radius" />)}
            {field("Unit", <select className="h-9 w-full rounded-md border bg-background px-2 text-sm" value={f.radius_unit} onChange={set("radius_unit")} data-testid="stage1-radius-unit"><option value="mi">Miles</option><option value="km">Kilometres</option></select>)}
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.exclude_holdings} onChange={set("exclude_holdings")} className="h-4 w-4 accent-[hsl(var(--primary))]" data-testid="stage1-exclude-holdings" />Leave out companies named "Holdings" or "Group"</label>
          <p className="text-xs text-muted-foreground">UK only (Companies House). Distance is measured from each company's registered office, which can differ from where it trades.</p>
        </div>
        <DialogFooter className="items-center">
          {(!sic.length || f.location.trim().length < 2) && <p className="mr-auto text-xs text-amber-600 dark:text-amber-300" data-testid="stage1-new-missing">{!sic.length ? "Tick at least one trade code" : "Type a town or postcode to search around"}</p>}
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="stage1-new-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || !sic.length || f.location.trim().length < 2} data-testid="stage1-new-submit">{busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Search Companies House</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
